import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useAuth } from '../context/AuthContext';
import Link from 'next/link';

export default function DeveloperPortal() {
  const { isAuthenticated, loading } = useAuth();
  const router = useRouter();
  const [apiStatus, setApiStatus] = useState(null);
  const [userLimits, setUserLimits] = useState(null);
  const [limitsLoading, setLimitsLoading] = useState(true);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      router.push({
        pathname: '/login',
        query: { redirect: router.asPath }
      });
    }
  }, [isAuthenticated, loading, router]);

  useEffect(() => {
    // Fetch API status (public for anonymous; richer payload when authed).
    const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';
    const token = typeof window !== 'undefined' ? localStorage.getItem('access_token') : null;
    fetch(`${apiUrl}/v1/users/status/`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    })
      .then(res => (res.ok ? res.json() : null))
      .then(data => { if (data) setApiStatus(data); })
      .catch(err => console.error('Failed to fetch API status:', err));
  }, [isAuthenticated]);

  // Fetch user's subscription/API limits (optional, for personalization)
  useEffect(() => {
    if (isAuthenticated) {
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';
      const token = localStorage.getItem('access_token');

      fetch(`${apiUrl}/v1/users/api-limits/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
        .then(res => res.json())
        .then(data => {
          setUserLimits(data);
          setLimitsLoading(false);
        })
        .catch(err => {
          console.error('Failed to fetch user limits:', err);
          setLimitsLoading(false);
        });
    } else {
      setLimitsLoading(false);
    }
  }, [isAuthenticated]);

  // Don't render the page if not authenticated or still loading
  if (!isAuthenticated || loading) {
    return (
      <div className="flex-1 flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const codeExamples = [
    {
      name: 'Python',
      icon: '🐍',
      description: 'Multiple frameworks',
      examples: ['Django', 'Flask', 'FastAPI', 'Requests'],
      link: 'https://github.com/Creole-Centric/api-examples/tree/main/python'
    },
    {
      name: 'JavaScript',
      icon: '📜',
      description: 'Embeddable widget & Node.js',
      examples: ['Embeddable Widget', 'Node.js'],
      link: 'https://github.com/Creole-Centric/api-examples/tree/main/javascript'
    },
    {
      name: 'TypeScript',
      icon: '📘',
      description: 'Fully typed interfaces',
      examples: ['Next.js', 'React', 'Express'],
      link: 'https://github.com/Creole-Centric/api-examples/tree/main/typescript'
    },
    {
      name: 'Go',
      icon: '🔷',
      description: 'Idiomatic Go',
      examples: ['CLI Example'],
      link: 'https://github.com/Creole-Centric/api-examples/tree/main/go'
    },
    {
      name: 'Swift',
      icon: '🍎',
      description: 'iOS/macOS native',
      examples: ['SwiftUI', 'UIKit'],
      link: 'https://github.com/Creole-Centric/api-examples/tree/main/swift'
    },
    {
      name: 'Java',
      icon: '☕',
      description: 'Java 11+',
      examples: ['Spring Boot', 'Android', 'Maven/Gradle'],
      link: 'https://github.com/Creole-Centric/api-examples/tree/main/java'
    },
    {
      name: 'C#/.NET',
      icon: '💎',
      description: '.NET 6.0+',
      examples: ['ASP.NET Core', 'Blazor', 'MAUI', 'Xamarin'],
      link: 'https://github.com/Creole-Centric/api-examples/tree/main/csharp'
    }
  ];

  const quickLinks = [
    {
      title: 'API Reference',
      href: '/developer/api-reference',
      icon: '📚',
      description: 'Complete API documentation with examples'
    },
    {
      title: 'Getting Started',
      href: '/developer/quickstart',
      icon: '🚀',
      description: '5-minute integration guide'
    },
    {
      title: 'Code Examples',
      href: 'https://github.com/Creole-Centric/api-examples',
      icon: '💻',
      description: '7+ languages with platform examples',
      external: true
    },
    {
      title: 'API Keys',
      href: '/api-keys',
      icon: '🔑',
      description: 'Manage your API keys and permissions'
    },
    {
      title: 'MCP Server',
      href: '/developer/mcp',
      icon: '🔌',
      description: 'Connect Claude, Cursor, and other MCP clients',
      badge: 'NEW'
    },
    {
      title: 'API Changelog',
      href: '/developer/changelog',
      icon: '📝',
      description: 'API updates and breaking changes'
    },
    {
      title: 'API Documentation',
      href: '/docs/documentation',
      icon: '📖',
      description: 'Comprehensive guides and tutorials'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 dark:from-blue-800 dark:to-indigo-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h1 className="text-4xl font-bold text-white mb-4">
            CreoleCentric Developer Portal
          </h1>
          <p className="text-xl text-blue-100">
            Build powerful applications with our Haitian Creole text-to-speech API.
            Production-ready code examples in 7+ languages.
          </p>

          {/* API Status Banner */}
          {apiStatus && (
            <div className="mt-6 inline-flex items-center bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 text-white">
              <div className={`w-2 h-2 rounded-full mr-2 ${
                apiStatus.status === 'operational' ? 'bg-green-400 animate-pulse' : 'bg-red-400'
              }`}></div>
              <span className="text-sm font-medium">
                API Status: {apiStatus.status} • Core v{apiStatus.version}
                {apiStatus.engine_version && ` • Engine v${apiStatus.engine_version}`}
                {` • API v${apiStatus.api_contract_version}`}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Quick Start CTA */}
        <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 mb-12">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-green-500 text-white text-2xl">
                ⚡
              </div>
            </div>
            <div className="ml-4 flex-1">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Get Started in 5 Minutes
              </h3>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                {isAuthenticated
                  ? "Create an API key, make your first request, and start building with Haitian Creole TTS."
                  : "Subscribe to a Creator plan or purchase a Creator Pack to get API access and start building with Haitian Creole TTS."}
              </p>
              <div className="mt-4 flex space-x-4">
                <Link
                  href="/developer/quickstart"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700"
                >
                  Quick Start Guide →
                </Link>
                {isAuthenticated ? (
                  <Link
                    href="/api-keys"
                    className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Get API Key
                  </Link>
                ) : (
                  <Link
                    href="/subscription"
                    className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    View Plans
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* MCP Spotlight — surface the new capability above the Quick Links grid */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-700 dark:to-purple-700 rounded-lg p-6 mb-12 shadow-lg">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="flex-shrink-0 flex items-center justify-center w-14 h-14 rounded-lg bg-white/15 backdrop-blur-sm text-3xl">
              🔌
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-400/20 text-green-200 ring-1 ring-green-300/30">
                  NEW
                </span>
                <h3 className="text-lg font-semibold text-white">
                  Model Context Protocol support
                </h3>
              </div>
              <p className="text-sm text-indigo-100">
                Connect CreoleCentric to Claude Desktop, Claude Code, Cursor, and other MCP-aware
                clients. 24 tools cover TTS, translation, dictionary lookup, STT, and account info.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link
                href="/developer/mcp"
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md bg-white text-indigo-700 hover:bg-indigo-50"
              >
                Set up MCP →
              </Link>
              <a
                href="https://modelcontextprotocol.io"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md bg-white/10 text-white hover:bg-white/20 ring-1 ring-white/30"
              >
                What is MCP?
              </a>
            </div>
          </div>
        </div>

        {/* Quick Links Grid */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Quick Links</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {quickLinks.map((link, index) => (
              link.external ? (
                <a
                  key={index}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-6 bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-lg transition-shadow border border-gray-200 dark:border-gray-700"
                >
                  <div className="text-3xl mb-3">{link.icon}</div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                    {link.title}
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{link.description}</p>
                </a>
              ) : (
                <Link
                  key={index}
                  href={link.href}
                  className="block p-6 bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-lg transition-shadow border border-gray-200 dark:border-gray-700"
                >
                  <div className="text-3xl mb-3">{link.icon}</div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                    {link.title}
                    {link.badge && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200 ring-1 ring-green-500/30">
                        {link.badge}
                      </span>
                    )}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{link.description}</p>
                </Link>
              )
            ))}
          </div>
        </div>

        {/* Code Examples Section */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Code Examples</h2>
            <a
              href="https://github.com/Creole-Centric/api-examples"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:underline"
            >
              View on GitHub
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {codeExamples.map((example, index) => (
              <a
                key={index}
                href={example.link}
                target="_blank"
                rel="noopener noreferrer"
                className="block p-6 bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-lg transition-all border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-400"
              >
                <div className="text-4xl mb-3">{example.icon}</div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{example.name}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{example.description}</p>
                <div className="space-y-1">
                  {example.examples.map((ex, i) => (
                    <div key={i} className="text-xs text-blue-600 dark:text-blue-400">• {ex}</div>
                  ))}
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* API Environment & Version Management */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-8 mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">API Environment & Version Management</h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Version Release Timeline</h3>
              <div className="space-y-3">
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 flex items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 text-sm font-bold mr-3">
                    30
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Days in staging</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Test new versions before production</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 flex items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900 text-yellow-600 dark:text-yellow-400 text-sm font-bold mr-3">
                    15
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Days dual-version support</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Both versions active in production</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 flex items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400 text-sm font-bold mr-3">
                    6
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Months deprecation period</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Old version supported after new release</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 flex items-center justify-center rounded-full bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400 text-sm font-bold mr-3">
                    7
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Total months to migrate</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">From staging to version sunset</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">API Environments</h3>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 space-y-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">Production</span>
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                      Live
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">api.creolecentric.com</p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">Engine: {apiStatus?.engine_version || 'Loading...'}</p>
                </div>

                <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">Staging</span>
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                      Live
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">api-staging.creolecentric.com</p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">Test new versions early</p>
                </div>

                <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    <strong className="text-gray-900 dark:text-white">Version Policy:</strong> Breaking changes require new major versions. We support 2 major versions simultaneously with 6-month deprecation windows.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                <strong className="text-blue-900 dark:text-blue-200">Version Lifecycle:</strong> New API versions are released to staging for 30 days of testing, then to production with 15 days of dual-version support, followed by a 3-month deprecation period. Total migration window: 4+ months.
              </p>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <Link
              href="/developer/api-stability"
              className="text-blue-600 dark:text-blue-400 hover:underline text-sm font-medium"
            >
              View full API environment & stability policy →
            </Link>
          </div>
        </div>

        {/* Support Section */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Need Help?</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Our team is here to help you build amazing applications with CreoleCentric API.
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            <a
              href="mailto:support@creolecentric.com"
              className="flex items-center p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
            >
              <div className="text-2xl mr-3">📧</div>
              <div>
                <div className="font-medium text-gray-900 dark:text-white">Email Support</div>
                <div className="text-xs text-gray-600 dark:text-gray-400">24-48 hour response</div>
              </div>
            </a>
            <Link
              href="/docs/help-center"
              className="flex items-center p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
            >
              <div className="text-2xl mr-3">❓</div>
              <div>
                <div className="font-medium text-gray-900 dark:text-white">Help Center</div>
                <div className="text-xs text-gray-600 dark:text-gray-400">FAQs & tutorials</div>
              </div>
            </Link>
            <a
              href="https://github.com/Creole-Centric/api-examples/issues"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
            >
              <div className="text-2xl mr-3">🐛</div>
              <div>
                <div className="font-medium text-gray-900 dark:text-white">Report Issues</div>
                <div className="text-xs text-gray-600 dark:text-gray-400">GitHub Issues</div>
              </div>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
